// ─── Legal pages ──────────────────────────────────────────────────────
// Ported from dane/frontend/app/etf-website/data/legal.ts. Content rewritten
// from the source `LegalPage`/`LegalSection` shape into the brief-mandated
// `LegalPageContent`/`LegalSection` shape (id-keyed sections with htmlContent,
// hero metadata, and a tabs array consumed by LegalPageRenderer).
//
// The htmlContent strings are our own static copy (not user input) so the
// LegalPageRenderer renders them via `dangerouslySetInnerHTML`. Inline tag
// styling matches the source: anchor tags use the orange accent classes.

export interface LegalSection {
  id: string;
  /** Short label used in the sidebar TOC navigation. */
  title: string;
  /**
   * Optional full heading rendered above the section body.
   * When present, the body heading uses this text; the TOC still uses `title`.
   * Used on the Privacy page where canonical TOC labels are short but body
   * headings are fully spelled out (e.g. "Use & Disclosure" in TOC vs
   * "Use and Disclosure of Information" in the body).
   */
  bodyTitle?: string;
  htmlContent: string;
}

export interface LegalTab {
  id: string;
  label: string;
  href: string;
}

export interface LegalHero {
  title: string;
  effectiveDate: string;
  entityBadge: string;
}

export interface LegalPageContent {
  hero: LegalHero;
  intro?: string;
  tabs: LegalTab[];
  sections: LegalSection[];
}

// ─── Shared tabs (the sticky tab nav shows all three legal pages) ────
const LEGAL_TABS: LegalTab[] = [
  { id: "privacy", label: "Privacy Policy", href: "/privacy" },
  { id: "terms", label: "Terms & Conditions", href: "/terms" },
  {
    id: "social-media-disclaimer",
    label: "Social Media Disclaimer",
    href: "/social-media-disclaimer",
  },
];

const EFFECTIVE_DATE = "2026";
const ENTITY_BADGE = "Corgi Strategies, LLC";

// ─── Inline link helper — keeps anchor styling consistent across copy ─
const A_CLASS = "text-[var(--etf-orange)] no-underline hover:opacity-80 transition-opacity";

// ─── PRIVACY ─────────────────────────────────────────────────────────
export const PRIVACY: LegalPageContent = {
  hero: {
    title: "Privacy Policy",
    effectiveDate: EFFECTIVE_DATE,
    entityBadge: ENTITY_BADGE,
  },
  tabs: LEGAL_TABS,
  intro: `<p>Corgi Strategies, LLC and its affiliate companies (collectively, “Corgi Strategies,” “Corgi Funds,” “we,” “us,” or “our”) recognize the importance of protecting the privacy of our website visitors. This Privacy Policy and Terms of Use (the “Policy”) describe how we collect, use, store, and disclose information when you visit our website located at <a href="https://corgifunds.com" class="${A_CLASS}">https://corgifunds.com</a> (the “Site”).</p><p>Corgi Strategies is committed to respecting the privacy of individuals who access or use this Site. This Policy is intended to inform you of our practices regarding the collection, use, and disclosure of information that you may provide through the Site. Please read this Policy carefully, along with any other policies or disclosures made available on the Site, before using the Site or submitting information to us.</p><p>Your use of this Site constitutes your consent, where required under applicable federal and state laws and regulations, to the collection, use, and disclosure of information as described in this Policy.</p>`,
  sections: [
    {
      id: "information-we-collect",
      title: "Information We Collect",
      htmlContent:
        "<p>Corgi Strategies may collect non-public information from a variety of sources. In connection with your use of the Site and any tools, forms, or features provided on it, we may collect certain non-public personal information from you or your authorized representative for purposes including, but not limited to: responding to inquiries or requests; providing information, data, or analysis; operating and improving the Site; and fulfilling legal, regulatory, or compliance obligations.</p>" +
        "<p>Like many websites, we may automatically collect information related to your interactions with the Site, including pages visited, search queries, IP addresses, browser type, and use of any proprietary or interactive tools.</p>" +
        "<p>If you voluntarily provide information to us, we may also collect and retain additional non-public personal information, which may include personal identifiers such as name, email address, IP address, investment objectives, transaction-related information, tax-related information, or other sensitive data.</p>",
    },
    {
      id: "use-and-disclosure",
      title: "Use & Disclosure",
      bodyTitle: "Use and Disclosure of Information",
      htmlContent:
        "<p>We do not disclose non-public personal information about our customers or website users except as permitted or required by law, or with the consent of the individual.</p>" +
        "<p>We may share information with certain unaffiliated third parties that provide services on our behalf, such as marketing, analytics, technology, compliance, media, or research service providers, solely to the extent necessary to perform such services or to process your requests. We do not sell customer or website user information to third parties.</p>",
    },
    {
      id: "data-security",
      title: "Data Security",
      bodyTitle: "Data Security and Retention",
      htmlContent:
        "<p>Corgi Strategies maintains physical, electronic, and procedural safeguards designed to protect non-public personal information. Our internal policies restrict access to such information and prohibit unauthorized use or disclosure.</p>" +
        "<p>We retain non-public personal information only for as long as reasonably necessary to fulfill the purposes for which it was collected, including satisfying legal, regulatory, accounting, or reporting requirements. When information is no longer required, it is securely deleted or destroyed in accordance with applicable laws and regulations.</p>",
    },
    {
      id: "additional-notices",
      title: "Additional Notices",
      bodyTitle: "Additional Privacy Notices",
      htmlContent:
        "<p>If you are an investor in any Corgi Funds, you may also receive additional privacy notices through fund documentation, regulatory filings, or transaction-related materials, which may supplement or further describe our privacy practices.</p>",
    },
    {
      id: "marketing-opt-out",
      title: "Marketing & Opt-Out",
      bodyTitle: "Marketing Communications and Opt-Out",
      htmlContent: `<p>You may opt out of permitting us to use non-public personally identifiable information collected through the Site for marketing or informational communications (such as email updates) by contacting us at <a href="mailto:info@corgifunds.com" class="${A_CLASS}">info@corgifunds.com</a>. You may also opt out by clicking the “unsubscribe” link included in any marketing email and following the provided instructions.</p>`,
    },
    {
      id: "website-disclaimer",
      title: "Website Disclaimer",
      bodyTitle: "Website Content Disclaimer",
      htmlContent:
        "<p>Corgi Strategies endeavors to present timely and accurate information on this Site; however, errors or omissions may occur. We make no representations or warranties, express or implied, regarding the accuracy, completeness, or reliability of any content on the Site.</p>" +
        "<p>Neither Corgi Strategies nor its service providers shall be liable for any inaccuracies, errors, omissions, delays, or actions taken in reliance on information contained on or omitted from the Site.</p>",
    },
    {
      id: "no-investment-advice",
      title: "No Investment Advice",
      htmlContent:
        "<p>Nothing on this Site constitutes investment advice, a recommendation, or an offer to buy or sell any security, fund, or investment strategy. Any information provided is general in nature and is not intended to be tailored to the investment objectives or financial circumstances of any particular individual.</p>" +
        "<p>Before making any investment decision, you should consult your own legal, tax, and financial advisers. Past performance is not indicative of future results.</p>",
    },
    {
      id: "terms-and-indemnification",
      title: "Terms & Indemnification",
      bodyTitle: "Terms of Use and Indemnification",
      htmlContent:
        "<p>As a condition of your use of this Site, you agree to be bound by the terms set forth herein and to indemnify and hold harmless Corgi Strategies, its affiliates, and their respective officers, directors, employees, and agents from and against any claims, losses, liabilities, costs, or expenses (including attorneys’ fees) arising out of or related to your use of the Site or violation of this Policy.</p>",
    },
    {
      id: "intellectual-property",
      title: "Intellectual Property",
      htmlContent:
        "<p>All content, information, trademarks, logos, designs, products, and services available on or through this Site are owned, licensed, or controlled by Corgi Strategies and are protected by applicable U.S. copyright, trademark, and intellectual property laws.</p>",
    },
    {
      id: "amendments",
      title: "Amendments",
      htmlContent: `<p>Corgi Strategies reserves the right to amend or update this Policy at any time without prior notice by posting the revised version on the Site. Your continued use of the Site following any such update constitutes acceptance of the revised Policy.</p><p>For questions regarding this Policy or our terms of use, please contact us at <a href="mailto:info@corgifunds.com" class="${A_CLASS}">info@corgifunds.com</a>.</p>`,
    },
    {
      id: "california-residents",
      title: "California Residents",
      bodyTitle: "Notice to California Residents",
      htmlContent:
        "<p>For users who are residents of the State of California, this notice is provided in accordance with the California Consumer Privacy Act of 2018 (“CCPA”), as amended, and applicable California privacy regulations. Certain rights and obligations under the CCPA may not apply in all circumstances and may depend on your relationship with Corgi Strategies.</p>" +
        "<p>We will not discriminate against you for exercising any rights available to you under applicable California privacy laws. Verification requirements and statutory exemptions may apply to certain requests, including those submitted by authorized agents.</p>",
    },
  ],
};

// ─── TERMS ───────────────────────────────────────────────────────────
export const TERMS: LegalPageContent = {
  hero: {
    title: "Terms & Conditions",
    effectiveDate: EFFECTIVE_DATE,
    entityBadge: ENTITY_BADGE,
  },
  tabs: LEGAL_TABS,
  intro: `<p>By accessing or using this Site, you agree to be legally bound by these Terms &amp; Conditions. If you do not agree to these terms, please do not use this Site. These Terms &amp; Conditions should be read in conjunction with our <a href="/privacy" class="${A_CLASS}">Privacy Policy</a>, which is incorporated herein by reference.</p>`,
  sections: [
    {
      id: "terms-of-use",
      title: "Terms of Use",
      htmlContent:
        "<p>This Site is provided for informational purposes only. You agree to use this Site only for lawful purposes and in a manner that does not infringe the rights of, restrict, or inhibit anyone else’s use and enjoyment of the Site.</p>" +
        "<p>Prohibited behavior includes harassing or causing distress or inconvenience to any other user, transmitting obscene or offensive content, or disrupting the normal flow of dialogue within this Site.</p>",
    },
    {
      id: "indemnification",
      title: "Indemnification",
      htmlContent:
        "<p>As a condition of your use of this Site, you agree to indemnify and hold harmless Corgi Strategies, its affiliates, and their respective officers, directors, employees, and agents from and against any claims, losses, liabilities, costs, or expenses (including attorneys’ fees) arising out of or related to your use of the Site or violation of these Terms &amp; Conditions.</p>" +
        "<p>Corgi Strategies reserves the right, at its own expense, to assume the exclusive defense and control of any matter otherwise subject to indemnification by you, in which event you will cooperate with Corgi Strategies in asserting any available defenses.</p>",
    },
    {
      id: "intellectual-property",
      title: "Intellectual Property",
      htmlContent:
        "<p>All content, information, trademarks, logos, designs, products, and services available on or through this Site are owned, licensed, or controlled by Corgi Strategies and are protected by applicable U.S. copyright, trademark, and intellectual property laws.</p>" +
        "<p>You may not reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, republish, download, store, or transmit any of the material on the Site without prior written consent from Corgi Strategies, except as follows: your computer may temporarily store copies of such materials incidental to your accessing and viewing those materials, and you may store files that are automatically cached by your web browser for display enhancement purposes.</p>",
    },
    {
      id: "no-investment-advice",
      title: "No Investment Advice",
      htmlContent:
        "<p>Nothing on this Site constitutes investment advice, a recommendation, or an offer to buy or sell any security, fund, or investment strategy. Any information provided is general in nature and is not intended to be tailored to the investment objectives or financial circumstances of any particular individual.</p>" +
        "<p>Before making any investment decision, you should consult your own legal, tax, and financial advisers. Past performance is not indicative of future results. Investing involves risk, including the possible loss of principal.</p>",
    },
    {
      id: "website-disclaimer",
      title: "Website Disclaimer",
      htmlContent:
        "<p>Corgi Strategies endeavors to present timely and accurate information on this Site; however, errors or omissions may occur. We make no representations or warranties, express or implied, regarding the accuracy, completeness, or reliability of any content on the Site. The Site and all content therein are provided on an “as is” and “as available” basis without any warranties of any kind.</p>" +
        "<p>Neither Corgi Strategies nor its service providers shall be liable for any inaccuracies, errors, omissions, delays, or actions taken in reliance on information contained on or omitted from the Site. In no event shall Corgi Strategies be liable for any indirect, special, incidental, or consequential damages arising out of or in connection with the use or inability to use this Site.</p>",
    },
    {
      id: "amendments",
      title: "Amendments",
      htmlContent: `<p>Corgi Strategies reserves the right to amend or update these Terms &amp; Conditions at any time without prior notice by posting the revised version on the Site. Your continued use of the Site following any such update constitutes your acceptance of the revised terms.</p><p>For questions regarding these Terms &amp; Conditions, please contact us at <a href="mailto:info@corgifunds.com" class="${A_CLASS}">info@corgifunds.com</a>.</p>`,
    },
  ],
};

// ─── SOCIAL MEDIA DISCLAIMER ─────────────────────────────────────────
export const SOCIAL_DISCLAIMER: LegalPageContent = {
  hero: {
    title: "Social Media Disclaimer",
    effectiveDate: EFFECTIVE_DATE,
    entityBadge: ENTITY_BADGE,
  },
  tabs: LEGAL_TABS,
  sections: [
    {
      id: "social-media-disclaimer",
      title: "Social Media Disclaimer",
      htmlContent:
        "<p>Corgi Strategies, LLC and its affiliates, including Corgi Funds (collectively, “Corgi Strategies”), and their respective employees, interns, investors, industry participants, and other affiliated parties may maintain a presence on social media platforms. All content shared through these channels is provided for informational and educational purposes only.</p>" +
        "<p>No post, comment, repost, like, share, or other interaction by Corgi Strategies or its affiliates should be construed as investment advice, a recommendation, or an offer to buy or sell any security or investment product.</p>" +
        "<p>Corgi Strategies does not guarantee the accuracy or completeness of information provided by third parties and is not responsible for any third-party content, links, or materials shared or referenced on its social media platforms.</p>" +
        "<p>It is not possible to invest directly in an index.</p>" +
        "<p>Past performance is not indicative of future results.</p>",
    },
  ],
};
